import {
	Component,
	OnInit,
	Input,
	ChangeDetectionStrategy,
	EventEmitter,
	ViewChildren,
	QueryList,
	ViewChild,
	OnDestroy,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { showErrorSwal, showErrorSwalHtml, toCaption } from '@asamco/utils';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, map, pairwise, takeUntil, tap } from 'rxjs/operators';
import { AccordionItemComponent } from '../../accordion/accordion-item/accordion-item.component';
import { AccessLevel } from '../../enums/access-level.enum';
import { Link } from '../../interfaces/link.interface';
import { LinkSettingView } from '../../interfaces/link-setting-view.interface';
import { ApplicationSettingsService } from '../../services/application-settings.service';
import { LinkService } from '../../services/link.service';
import { LinkEditLoginDetailsComponent } from '../link-edit-login-details/link-edit-login-details.component';
import { DescriptionModalComponent } from '../description-modal/description-modal.component';
import { MarketPlaceItem } from '../../interfaces/market-place-item.interface';
import notify from 'devextreme/ui/notify';
import { DataType } from '../../enums/data-type.enum';
import { LinkEditSettingGroupingAdminComponent } from './link-edit-setting-grouping-admin/link-edit-setting-grouping-admin.component';
import { AsamcoUserService } from '@asamco/base';
import { SettingTemplateGroupingService } from '../../services/setting-template-grouping.service';
import { SettingTemplateGrouping } from '../../interfaces/link-setting-template-grouping.interface';
import { AlCompanyService } from '../../services/al-company.service';
import { SettingSectionSortPipe, SettingGroupNameSortPipe, SettingSortPipe } from '../../pipes/section_sortin.pipe';
import { LinkLogSettingsComponent } from '../link-log-settings/link-log-settings.component';
import { LinkEditSettingsComponent } from '../link-edit-settings/link-edit-settings.component';
import { LinkEditGeneralComponent } from '../link-edit-general/link-edit-general.component';
import { DxiItemModule } from 'devextreme-angular/ui/nested';
import { DxAccordionModule } from 'devextreme-angular';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

export interface SectionSetting {
	section: string;
	accessGroups: SectionAccessGroup[];
	defaultAccess: SectionAccessGroup;
}

export interface SectionAccessGroup {
	accessLevel: AccessLevel;
	groups: SectionGroup[];
	formGroup?: FormGroup;
}

export interface SectionGroup {
	name: string;
	settings: LinkSettingView[];
}

@Component({
    selector: 'al-link-edit-setup',
    templateUrl: './link-edit-setup.component.html',
    styleUrls: ['./link-edit-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        DescriptionModalComponent,
        NgIf,
        DxAccordionModule,
        DxiItemModule,
        LinkEditGeneralComponent,
        FormsModule,
        ReactiveFormsModule,
        LinkEditLoginDetailsComponent,
        NgFor,
        LinkEditSettingsComponent,
        LinkLogSettingsComponent,
        LinkEditSettingGroupingAdminComponent,
        AsyncPipe,
        SettingSectionSortPipe,
        SettingGroupNameSortPipe,
        SettingSortPipe,
    ],
})
export class LinkEditSetupComponent implements OnInit, OnDestroy {
	toCaption = toCaption;

	private readonly destroy$ = new Subject<void>();

	private readonly constantFormGroupNames: string[] = ['general', 'log', 'logins'];

	/** For using the enum in html */
	AccessLevel: typeof AccessLevel = AccessLevel;

	/** The link to edit */
	@Input()
	link: Link;

	@Input()
	linkInfo: MarketPlaceItem;

	@ViewChildren(AccordionItemComponent)
	accordionItems: QueryList<AccordionItemComponent>;

	@ViewChild(LinkEditLoginDetailsComponent)
	appLogins: LinkEditLoginDetailsComponent;

	@ViewChild(DescriptionModalComponent)
	descriptionModal: DescriptionModalComponent;

	@ViewChild(LinkEditSettingGroupingAdminComponent)
	adminEditSettingsGrouping: LinkEditSettingGroupingAdminComponent;

	validateEvent: EventEmitter<any> = new EventEmitter();

	LinkSettings$: Observable<SectionSetting[]>;

	toastText: string = 'Successfully saved settings';
	toastMessageType: string = 'success';
	toastVisible: boolean = true;

	public dateFormat: string = '';

	manuallyPatched: boolean = false;

	formGroupCollection: FormGroup;
	private sections: string[] = [];
	private IdtoLinkSettingIdMap: Map<number, number> = new Map<number, number>();

	public isAdminSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public linkSettingGroupings: Observable<SettingTemplateGrouping[]> = new Observable<SettingTemplateGrouping[]>();

	//public LinkSettingsSubject = new BehaviorSubject<LinkSettingView[]>(null)

	constructor(
		private asamcoUserService: AsamcoUserService,
		private applicationSettingsService: ApplicationSettingsService,
		private linkService: LinkService,
		private settingTemplateGroupingService: SettingTemplateGroupingService,
		private companyService: AlCompanyService
	) {}

	ngOnInit(): void {
		this.asamcoUserService.isAdmin().then(result => {
			this.isAdminSubject.next(result);
		});

		this.companyService.getCompanyDateFormat().subscribe(e => {
			this.dateFormat = e;
		});

		this.linkSettingGroupings = this.settingTemplateGroupingService.getAllLinkSettingGroupings();

		/** This gathers the to and from link settings,
		 *	The map takes the single array and groups all linksettings in their respective group
		 * 	the settings are grouped into sectionName, AccessLevel, GroupName.
		 *  Then all links in their groupname are sorted so they should always be displayed the same.
		 *  tap makes sure the form gets initialized,
		 */
		this.LinkSettings$ = this.applicationSettingsService.get(this.link.id).pipe(
			map((items: Array<LinkSettingView>) => {
				//this.LinkSettingsSubject.next(items);
				const result: SectionSetting[] = [];

				const getOrCreateSection = (sectionName: string) => {
					let section: SectionSetting = result.find(s => s.section === sectionName);
					if (!section) {
						section = {
							section: sectionName,
							accessGroups: [],
							defaultAccess: null,
						};
						result.push(section);
					}

					return section;
				};

				const getOrCreateAccessGroup = (section: SectionSetting, access: AccessLevel) => {
					//There is no accessLevel or its normal and doesnt have to be in an accordion.
					if (access <= 1) {
						let accessGroupDefault: SectionAccessGroup = section.defaultAccess;
						if (!accessGroupDefault) {
							accessGroupDefault = {
								accessLevel: AccessLevel.normal,
								groups: [],
							};
							section.defaultAccess = accessGroupDefault;
						}
						return accessGroupDefault;
					}
					let accessGroupFind: SectionAccessGroup = section.accessGroups.find(ag => ag.accessLevel === access);
					if (!accessGroupFind) {
						accessGroupFind = {
							accessLevel: access,
							groups: [],
						};
						section.accessGroups.push(accessGroupFind);
					}

					return accessGroupFind;
				};

				const getOrCreateGroup = (SessionAccessGroup: SectionAccessGroup, groupName: string) => {
					let group: SectionGroup = SessionAccessGroup.groups.find(gr => gr.name === groupName);
					if (!group) {
						group = {
							name: groupName,
							settings: [],
						};
						SessionAccessGroup.groups.push(group);
					}

					return group;
				};
				items.forEach(item => {
					const section = getOrCreateSection(item.sectionName);
					const accessGroup = getOrCreateAccessGroup(section, item.accessLevel);
					const group = getOrCreateGroup(accessGroup, item.groupName);

					group.settings.push(item);
				});

				return result;
			}),
			tap(items => this.initForm(items))
		);
		// this.LinkSettings$.pipe(takeUntil(this.destroy$)).subscribe(e => {
		// 	//console.log(e);
		// });
	}

	ngOnDestroy(): void {
		// could still be initializing.
		if (this.formGroupCollection) {
			this.saveChangedForms(null, null);
		}
	}

	private initForm(sections: SectionSetting[]) {
		this.formGroupCollection = new FormGroup({});
		this.formGroupCollection.addControl('general', new FormGroup({ id: new FormControl(this.link.id) }));
		this.formGroupCollection.addControl('logins', new FormGroup({}));
		sections.forEach(section => {
			this.sections.push(section.section);

			this.initAccessForm(section.defaultAccess);
			this.formGroupCollection.addControl(section.section + '_' + section.defaultAccess.accessLevel, section.defaultAccess.formGroup);

			section.accessGroups.forEach(accessGroup => {
				this.initAccessForm(accessGroup);
				this.formGroupCollection.addControl(section.section + '_' + accessGroup.accessLevel, accessGroup.formGroup);
			});
		});

		this.formGroupCollection.addControl('log', new FormGroup({}));
		this.formGroupCollection.valueChanges
			.pipe(pairwise(), debounceTime(100), takeUntil(this.destroy$))
			.subscribe(([prev, next]: [any, any]) => this.saveChangedForms(prev, next));
	}

	private initAccessForm(accessGroup: SectionAccessGroup) {
		accessGroup.formGroup = new FormGroup({});
		accessGroup.groups.forEach(group => group.settings.forEach(setting => this.makeFormControl(setting, accessGroup.formGroup)));
	}

	private makeFormControl(setting: LinkSettingView, formGroup: FormGroup) {
		const validators = [];
		if (setting.required) validators.push(Validators.required);

		//Parse values for controls
		if (setting.dataType === DataType.Boolean) {
			//Parse true and false strings for checkboxes
			if (setting.useValue.toString().toLowerCase() == 'true') {
				setting.useValue = true;
			} else if (setting.useValue.toString().toLowerCase() == 'false') {
				setting.useValue = false;
			}
		}
		if (setting.dataType === DataType.DropDownMenu) {
			setting.useValue = parseInt(setting.useValue, 10);
		}

		const control = new FormControl(setting.useValue, { validators: validators, updateOn: 'change' });
		formGroup.addControl(setting.id.toString(), control);
		this.IdtoLinkSettingIdMap.set(setting.id, setting.linkSettingId);
	}

	public openAdminSettingManagementMenu() {
		this.adminEditSettingsGrouping.setupModal();
	}

	public saveChangedForms(eventData1: any, eventData: any) {
		if (this.manuallyPatched) {
			this.manuallyPatched = false;
			return;
		}

		if (!eventData1 && !eventData) {
			return;
		}

		let keySaved = null;
		let controlIdSaved = null;

		this.formGroupCollection.updateValueAndValidity({ emitEvent: false });
		const result = {};
		let foundChanges: boolean = false;
		const settingResults = {};
		for (const key in this.formGroupCollection.controls) {
			if (!this.constantFormGroupNames.includes(key)) {
				const abstractControl: AbstractControl = this.formGroupCollection.get(key);
				if (!abstractControl.dirty) continue;
				if (abstractControl instanceof FormGroup) {
					for (const controlName of Object.keys(abstractControl.controls)) {
						const settingControl = abstractControl.get(controlName);
						if (settingControl.dirty && settingControl.valid) {
							const settingResult = {
								value: settingControl.value,
								linkSettingId: this.IdtoLinkSettingIdMap.get(parseInt(controlName, 10)),
							};
							settingResults[controlName] = settingResult;
							foundChanges = true;
							keySaved = key;
							controlIdSaved = controlName;
						}
					}
				}
			}
		}
		result['settings'] = settingResults;
		const link = {};
		const generalFormGroup = this.formGroupCollection.get('general');
		if (generalFormGroup instanceof FormGroup)
			for (const controlName of Object.keys(generalFormGroup.controls)) {
				const control = generalFormGroup.get(controlName);
				if (control && control.dirty && control.valid) {
					link[controlName] = control.value;
					foundChanges = true;
				}
			}
		const logFormGroup = this.formGroupCollection.get('log');
		if (logFormGroup instanceof FormGroup)
			for (const controlName of Object.keys(logFormGroup.controls)) {
				const control = logFormGroup.get(controlName);
				if (control && control.dirty && control.valid) {
					link[controlName] = control.value;
					foundChanges = true;
				}
			}
		result['link'] = link;
		if (foundChanges) {
			result['linkId'] = this.link.id;
			this.formGroupCollection.disable();
			this.linkService.autoSave(result).subscribe({
				next: subResult => {
					this.formGroupCollection.enable();
					if (subResult) {
						this.formGroupCollection.markAsPristine();
						notify({
							message: 'Successfully autosaved settings',
							type: 'success',
							displayTime: 1000,
						});
					}
				},
				error: error => {
					//Set the value of the field to it's previous value (if an error occured)
					this.formGroupCollection.enable();
					if (controlIdSaved && keySaved) {
						(this.formGroupCollection.controls[keySaved] as FormGroup).controls[controlIdSaved].patchValue(
							eventData1[keySaved][controlIdSaved]
						);
						//One-time use boolean so we don't get stuck in a loop (since the patchValue triggers this function)
						this.manuallyPatched = true;
						showErrorSwalHtml(error);
					} else {
						//TODO: Fix for other fields than settings :\
						this.manuallyPatched = true;
						showErrorSwalHtml(error);
					}
				}
			});
		}
	}

	public isSectionInvalid(section: SectionSetting): boolean {
		return (
			(section.defaultAccess.formGroup.touched && section.defaultAccess.formGroup.invalid) ||
			section.accessGroups.some(access => access.formGroup.touched && access.formGroup.invalid)
		);
	}
}
